import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import throttle from 'lodash/throttle';
import * as React from 'react';
import { BookMeetingCalendarWithFormik } from '../BookMeetingCalendar/BookMeetingCalendarWithFormik';
import { FormikValidationErrorHandler } from '~/Features/ApplicationApplets/Shared/FormikValidationErrorHandler/FormikValidationErrorHandler';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import { ActivityIndicator } from '~/Partials/ActivityIndicator/ActivityIndicator';
import { ApplicationError } from '~/Partials/ApplicationError/ApplicationError';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { HtmlDiv } from '~/Partials/Controls/HtmlDiv';
import { FormFieldCheckbox } from '~/Partials/FormFields/FormFieldCheckbox';
import { FormFieldInputText } from '~/Partials/FormFields/FormFieldInputText';
import { FormFieldSelect } from '~/Partials/FormFields/FormFieldSelect';
import { FormFieldTextArea } from '~/Partials/FormFields/FormFieldTextArea';
import { formSubmitThrottleWaitMs } from '~/Utils/FormsUtils';
import { emDash } from '~/Utils/HTMLElements';
import styles from './BookMeetingForm.module.css';
import type { BookMeetingFormState, BookMeetingFormTranslations, TimeSlot } from './Types/types';
import { getTimeSlot, getValidators, onSubmit, updateTimeSlotsFieldOnDatePick } from './Utils/utils';

export interface BookMeetingFormProps {
    heading: string;
    submitUrl: string;
    termsAndConditionsText: HtmlString;
    onSubmitted: () => void;
    translations: BookMeetingFormTranslations;
    attendanceOptions: {
        [key: string]: string;
    };
    timeSlots: TimeSlot[];
}

export const BookMeetingForm: React.FC<BookMeetingFormProps> = props => {
    const { heading, termsAndConditionsText, translations, submitUrl, onSubmitted, attendanceOptions, timeSlots } =
        props;

    const validations = getValidators(translations);

    const handleSubmit = React.useMemo(
        () =>
            throttle(
                async (
                    data: BookMeetingFormState,
                    formikHelpers: FormikHelpers<BookMeetingFormState>,
                    formSubmitUrl: string,
                    onSubmittedFunction: () => void,
                    formTranslations: BookMeetingFormTranslations
                ) => {
                    await onSubmit(
                        data,
                        formikHelpers,
                        formSubmitUrl,
                        onSubmittedFunction,
                        formTranslations.genericError
                    );
                },
                formSubmitThrottleWaitMs
            ),
        []
    );

    const initialFormValues: BookMeetingFormState = React.useMemo(
        () => ({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            attendance: '',
            date: null,
            timeSlot: '',
            areTermsAccepted: false,
            comment: '',
        }),
        []
    );

    const renderCommentLabel = () => (
        <span>
            {translations.labelFieldComment}
            <span className={styles.Optional}>
                {emDash} {translations.fieldIsOptional}
            </span>
        </span>
    );

    return (
        <div>
            <h2 className={styles.Heading}>{heading}</h2>
            <Formik<BookMeetingFormState>
                initialValues={initialFormValues}
                validateOnBlur={false}
                onSubmit={(values, formikHelpers) => {
                    handleSubmit(values, formikHelpers, submitUrl, onSubmitted, translations);
                }}
            >
                {({ errors, isSubmitting, isValid, status, values }) => (
                    <FormikValidationErrorHandler<BookMeetingFormState>
                        isSubmitting={isSubmitting}
                        isValid={isValid}
                        errors={errors}
                    >
                        <Form noValidate className="Form">
                            <div className={styles.PersonalDetailsSection}>
                                <div className={styles.FullName}>
                                    <FormFieldInputText
                                        name="firstName"
                                        label={translations.labelFieldFirstName}
                                        maxLength={100}
                                        validateFieldOnFieldBlur
                                        validate={validations.firstName}
                                    />
                                    <FormFieldInputText
                                        name="lastName"
                                        label={translations.labelFieldLastName}
                                        maxLength={100}
                                        validateFieldOnFieldBlur
                                        validate={validations.lastName}
                                    />
                                </div>
                                <FormFieldInputText
                                    name="email"
                                    label={translations.labelFieldEmail}
                                    maxLength={100}
                                    validateFieldOnFieldBlur
                                    validate={validations.email}
                                    inputMode="email"
                                />
                                <FormFieldInputText
                                    name="phone"
                                    label={translations.labelFieldPhoneNumber}
                                    maxLength={100}
                                    validateFieldOnFieldBlur
                                    validate={validations.phoneNumber}
                                />
                                <FormFieldSelect
                                    name="attendance"
                                    label={translations.labelFieldAttendance}
                                    options={attendanceOptions}
                                    validate={validations.attendance}
                                    validateFieldOnFieldBlur
                                />
                                <div className={styles.DateAndTime}>
                                    <BookMeetingCalendarWithFormik
                                        name="date"
                                        translations={translations.calendarTranslations}
                                        validate={validations.date}
                                        validateFieldOnFieldBlur
                                    />
                                    <FormFieldSelect
                                        name="timeSlot"
                                        label={translations.labelFieldTimeSlot}
                                        options={getTimeSlot(values.date, timeSlots)}
                                        // Formik v2 removed 'formik: FormikContext' type from shouldUpdate, so 'any' is needed to avoid TS errors.
                                        shouldUpdate={(update: any) =>
                                            updateTimeSlotsFieldOnDatePick(update.formik, values)
                                        }
                                        disabled={values.date === null}
                                        validate={validations.timeSlot}
                                        validateFieldOnFieldBlur
                                    />
                                </div>
                                <FormFieldTextArea
                                    name="comment"
                                    label={renderCommentLabel()}
                                    placeholder={translations.placeholderFieldComment}
                                    autoGrow
                                />
                            </div>
                            <div className={styles.SubmitSection}>
                                <FormFieldCheckbox
                                    name="areTermsAccepted"
                                    validate={validations.isChecked}
                                    label={<HtmlDiv html={termsAndConditionsText} />}
                                    className={styles.TermsCheckbox}
                                />
                                <Button
                                    htmltype="submit"
                                    size={ButtonSize.Large}
                                    tier={ButtonTier.Secondary}
                                    type={ButtonType.Solid}
                                    className={styles.SubmitButton}
                                >
                                    {translations.labelSubmitButton}
                                </Button>
                            </div>
                            {status && <ApplicationError body={status} />}
                            <ActivityIndicator active={isSubmitting} cover />
                        </Form>
                    </FormikValidationErrorHandler>
                )}
            </Formik>
        </div>
    );
};
